// src/pages/Programs.js
/* =========================
FINAL PATCH (Step 2A):
- Allocation System (EQUAL / PERCENT / KG)
- For PERCENT/KG, send allocations[] as backend expects
- Hide coil_id and coil_in_id from UI (still used internally)
- Keep allocation keys synced with circle sizes (critical)
- Percent seeding guarantees total = 100.00 exactly
========================= */

import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../lib/auth";
import { api, getFriendlyErrorMessage } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Layers, Trash2, RefreshCcw } from "lucide-react";

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getCoilWeight = (coil) => {
  const kanta = safeNum(coil?.kanta_weight);
  const slip = safeNum(coil?.slip_weight);
  return kanta > 0 ? kanta : slip;
};

const normalizeSizes = (arr) => {
  const out = [];
  const seen = new Set();
  for (const s of arr || []) {
    const n = Number(s);
    if (!Number.isFinite(n) || n <= 0) continue;
    if (seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
};

const sizeKey = (n) => String(Number(n)); // consistent key for 8 and 8.0
const PROGRAM_DRAFT_KEY = "coildock:programs:draft:v1";

const Programs = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(["ADMIN", "OWNER", "MUNIM"]); // OPERATOR read-only

  const [coilIns, setCoilIns] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedCoilIn, setSelectedCoilIn] = useState(null);
  const [selectedCoilId, setSelectedCoilId] = useState(null);

  // Create Program modal
  const [createOpen, setCreateOpen] = useState(false);
  const [circleSizes, setCircleSizes] = useState([""]);
  const [notes, setNotes] = useState("");
  const [loadingCreate, setLoadingCreate] = useState(false);

  // allocation system
  const [allocationMode, setAllocationMode] = useState("EQUAL"); // EQUAL | PERCENT | KG
  const [allocBySize, setAllocBySize] = useState({}); // { "8": "50", "10": "50" } percent or kg

  // Rate per size (optional map)
  const [ratesBySize, setRatesBySize] = useState({}); // { "8": "2.5" }

  useEffect(() => {
    fetchCoilIns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCoilIn) fetchPrograms(selectedCoilIn);
    else setPrograms([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCoilIn]);

  useEffect(() => {
    setSelectedCoilId(null);
  }, [selectedCoilIn]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(PROGRAM_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft || !Array.isArray(draft.circleSizes)) return;
      if (window.confirm("Found unsaved Program draft. Restore it?")) {
        setSelectedCoilIn(draft.selectedCoilIn || null);
        setSelectedCoilId(draft.selectedCoilId || null);
        setCircleSizes(draft.circleSizes.length ? draft.circleSizes : [""]);
        setNotes(draft.notes || "");
        setAllocationMode(draft.allocationMode || "EQUAL");
        setAllocBySize(draft.allocBySize || {});
        setRatesBySize(draft.ratesBySize || {});
        toast.info("Program draft restored");
      }
    } catch {
      // ignore bad draft
    }
  }, []);

  useEffect(() => {
    try {
      const draft = {
        selectedCoilIn,
        selectedCoilId,
        circleSizes,
        notes,
        allocationMode,
        allocBySize,
        ratesBySize,
      };
      localStorage.setItem(PROGRAM_DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // ignore storage errors
    }
  }, [selectedCoilIn, selectedCoilId, circleSizes, notes, allocationMode, allocBySize, ratesBySize]);

  const fetchCoilIns = async () => {
    try {
      const response = await api.coilIns.list({ is_active: true });
      setCoilIns(response.data || []);
    } catch (err) {
      toast.error(getFriendlyErrorMessage(err, "Failed to fetch coil entries"), {
        action: { label: "Retry", onClick: () => fetchCoilIns() },
      });
    }
  };

  const fetchPrograms = async (coilInId) => {
    try {
      const response = await api.programs.list({ coil_in_id: coilInId });
      setPrograms(response.data || []);
    } catch (err) {
      toast.error(getFriendlyErrorMessage(err, "Failed to fetch programs"), {
        action: { label: "Retry", onClick: () => fetchPrograms(coilInId) },
      });
    }
  };

  const selectedCoilInDoc = useMemo(
    () => coilIns.find((c) => c.id === selectedCoilIn),
    [coilIns, selectedCoilIn]
  );

  const coilsInSelected = useMemo(() => selectedCoilInDoc?.coils || [], [selectedCoilInDoc]);

  const selectedCoilDoc = useMemo(() => {
    if (!selectedCoilId) return null;
    return (coilsInSelected || []).find((c) => c.coil_id === selectedCoilId) || null;
  }, [coilsInSelected, selectedCoilId]);

  const selectedCoilWeight = useMemo(() => {
    return selectedCoilDoc ? getCoilWeight(selectedCoilDoc) : 0;
  }, [selectedCoilDoc]);

  const totalWeightSelected = useMemo(() => {
    return (coilsInSelected || []).reduce((sum, c) => sum + getCoilWeight(c), 0);
  }, [coilsInSelected]);

  const programsByCoilId = useMemo(() => {
    const map = {};
    for (const p of programs || []) {
      const cid = p?.coil_id || "__NO_COIL__";
      map[cid] = map[cid] || [];
      map[cid].push(p);
    }

    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => {
        if ((b.is_active === true) !== (a.is_active === true)) return (b.is_active === true) - (a.is_active === true);
        const tb = b.created_at ? Date.parse(b.created_at) : 0;
        const ta = a.created_at ? Date.parse(a.created_at) : 0;
        return tb - ta;
      });
    }

    return map;
  }, [programs]);

  // =========================
  // Allocation seeding + syncing (CRITICAL)
  // =========================
  const seedPercentSplit100 = (sizes) => {
    const valid = normalizeSizes(sizes);
    if (valid.length === 0) return {};

    // Make exact 100.00 total with rounding.
    const parts = valid.length;
    const rawEach = 100 / parts;
    const roundedEach = Math.floor(rawEach * 100) / 100; // 2 decimals floor
    const out = {};
    for (let i = 0; i < parts; i++) {
      out[sizeKey(valid[i])] = String(roundedEach.toFixed(2));
    }
    const used = roundedEach * parts;
    const remainder = Math.round((100 - used) * 100) / 100; // 2 decimals
    // Add remainder to last key so total = 100.00
    const lastKey = sizeKey(valid[parts - 1]);
    out[lastKey] = String((Number(out[lastKey]) + remainder).toFixed(2));
    return out;
  };

  const syncAllocKeysWithSizes = (mode, sizes, prevAlloc) => {
    const valid = normalizeSizes(sizes);
    const next = {};

    // Keep existing values when possible
    for (const s of valid) {
      const k = sizeKey(s);
      if (prevAlloc && Object.prototype.hasOwnProperty.call(prevAlloc, k)) {
        next[k] = prevAlloc[k];
      } else {
        next[k] = mode === "KG" ? "" : "";
      }
    }

    if (mode === "PERCENT") {
      // If nothing meaningful exists yet (or keys changed), seed exact 100 split
      const hasAny = Object.values(next).some((v) => String(v ?? "").trim() !== "");
      if (!hasAny) return seedPercentSplit100(valid);
    }

    return next;
  };

  // Keep allocBySize always aligned with current circleSizes while modal open
  useEffect(() => {
    if (!createOpen) return;
    if (allocationMode === "EQUAL") return;

    setAllocBySize((prev) => syncAllocKeysWithSizes(allocationMode, circleSizes, prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [circleSizes, allocationMode, createOpen]);

  // =========================
  // CREATE PROGRAM (coil-wise)
  // =========================
  const resetCreateForm = () => {
    setCircleSizes([""]);
    setRatesBySize({});
    setNotes("");
    setAllocationMode("EQUAL");
    setAllocBySize({});
  };

  const addCircleSize = () => setCircleSizes((prev) => [...prev, ""]);

  const removeCircleSize = (index) => {
    setCircleSizes((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [""];
    });
  };

  const updateCircleSize = (index, value) => {
    setCircleSizes((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const updateRateForSize = (size, value) => {
    setRatesBySize((prev) => ({ ...prev, [sizeKey(size)]: value }));
  };

  const updateAllocForSize = (size, value) => {
    setAllocBySize((prev) => ({ ...prev, [sizeKey(size)]: value }));
  };

  const handleCreateProgram = async (e) => {
    e.preventDefault();
    if (loadingCreate) return;

    if (!canEdit) return toast.error("You do not have permission");
    if (!selectedCoilIn) return toast.error("Please select a coil entry");
    if (!selectedCoilId) return toast.error("Please select a specific coil");

    const validSizes = normalizeSizes(circleSizes);

    if (validSizes.length === 0) {
      return toast.error("Please enter at least one valid circle size");
    }

    // build rates_by_circle_size (optional)
    const ratesMap = {};
    for (const sz of validSizes) {
      const v = ratesBySize[sizeKey(sz)];
      if (v === undefined || v === null || String(v).trim() === "") continue;
      const r = Number(v);
      if (!Number.isFinite(r) || r < 0) return toast.error("Rate per kg must be a valid non-negative number");
      ratesMap[sizeKey(sz)] = r;
    }

    // allocations
    let allocations = null;

    if (allocationMode === "EQUAL") {
      allocations = null;
    } else {
      allocations = [];
      for (const sz of validSizes) {
        const raw = allocBySize[sizeKey(sz)];
        const n = Number(raw);
        if (!Number.isFinite(n) || n <= 0) {
          return toast.error(`Allocation value required for ${sz}" (must be > 0)`);
        }
        allocations.push({ circle_size: sz, value: n });
      }

      if (allocationMode === "PERCENT") {
        const total = allocations.reduce((s, a) => s + safeNum(a.value), 0);
        if (!(total >= 99 && total <= 101)) {
          return toast.error(`Percent allocations must total ~100 (current: ${total.toFixed(2)})`);
        }
      }

      if (allocationMode === "KG") {
        if (selectedCoilWeight > 0) {
          const totalKg = allocations.reduce((s, a) => s + safeNum(a.value), 0);
          if (totalKg > selectedCoilWeight * 1.02) {
            return toast.error(
              `Allocated KG (${totalKg.toFixed(2)}) is more than coil weight (${selectedCoilWeight.toFixed(2)}). Fix allocation.`
            );
          }
        }
      }
    }

    setLoadingCreate(true);
    try {
      await api.programs.create({
        coil_in_id: selectedCoilIn,
        coil_id: selectedCoilId,
        allocation_mode: allocationMode,
        circle_sizes: validSizes,
        allocations: allocations,
        rate_per_kg: null,
        rates_by_circle_size: Object.keys(ratesMap).length ? ratesMap : null,
        notes: notes?.trim() ? notes.trim() : null,
      });

      toast.success("Program created");
      setCreateOpen(false);
      resetCreateForm();
      localStorage.removeItem(PROGRAM_DRAFT_KEY);
      fetchPrograms(selectedCoilIn);
    } catch (error) {
      toast.error(getFriendlyErrorMessage(error, "Failed to create program"));
    } finally {
      setLoadingCreate(false);
    }
  };

  const AllocationInputs = ({ sizes }) => {
    const validSizes = normalizeSizes(sizes);

    if (allocationMode === "EQUAL") {
      return (
        <div className="text-[10px] font-mono text-slate-400 mt-1 uppercase">
          EQUAL mode: Backend splits coil weight equally among sizes.
        </div>
      );
    }

    return (
      <div className="mt-3 space-y-2">
        <div className="text-[10px] font-mono text-slate-400 uppercase">
          {allocationMode === "PERCENT"
            ? "Enter percent split per size (total must be ~100)."
            : "Enter KG allocation per size (sum should not exceed coil weight)."}
        </div>

        <div className="space-y-2">
          {validSizes.map((sz) => (
            <div key={sz} className="grid grid-cols-3 gap-2 items-end">
              <div>
                <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Size</label>
                <input 
                  value={`${sz}"`} 
                  disabled 
                  className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-slate-400 rounded-none focus:outline-none" 
                />
              </div>
              <div className="col-span-2">
                <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">
                  {allocationMode === "PERCENT" ? "Percent (%) *" : "KG *"}
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                  value={allocBySize[sizeKey(sz)] ?? ""}
                  onChange={(e) => updateAllocForSize(sz, e.target.value)}
                  placeholder={allocationMode === "PERCENT" ? "e.g. 50" : "e.g. 120.5"}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-industrial-bg text-white p-6 space-y-6" data-testid="programs-page">
      {/* Header */}
      <div className="border-b border-industrial-border pb-4">
        <h1 className="font-heading text-3xl font-bold tracking-tight uppercase">
          Program Management
        </h1>
        <p className="text-slate-400 mt-2 font-mono text-xs uppercase">
          Define programs per individual coil. Allocations: EQUAL / PERCENT / KG.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Coil-in selection + coil selection */}
        <div className="bg-industrial-s1 border border-industrial-border p-6 space-y-4">
          <div>
            <h2 className="text-xs uppercase font-sans tracking-wider text-slate-400 mb-2 font-semibold">
              Select Coil Entry
            </h2>
            <select 
              value={selectedCoilIn || ""} 
              onChange={(e) => setSelectedCoilIn(e.target.value || null)}
              className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
              data-testid="coil-select"
            >
              <option value="">Select a coil entry</option>
              {coilIns.map((coil) => (
                <option key={coil.id} value={coil.id}>
                  {coil.party_name || "Unknown Party"} - {new Date(coil.inward_datetime).toLocaleDateString()}
                  {coil.slip_no ? ` - Slip: ${coil.slip_no}` : ""}
                </option>
              ))}
            </select>
          </div>

          {selectedCoilInDoc && (
            <div className="p-3 bg-industrial-s2 border border-industrial-border text-xs font-mono space-y-1 text-slate-300">
              <p><span className="text-slate-500 uppercase">Party:</span> <span className="text-white font-bold">{selectedCoilInDoc.party_name || "-"}</span></p>
              <p><span className="text-slate-500 uppercase">Coils:</span> <span className="text-white font-bold">{coilsInSelected?.length || 0}</span></p>
              <p><span className="text-slate-500 uppercase">Total Weight:</span> <span className="text-white font-bold">{totalWeightSelected.toFixed(2)} kg</span></p>
            </div>
          )}

          {selectedCoilIn && (
            <div className="space-y-2 pt-2 border-t border-industrial-border/60">
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans font-semibold">Select Coil</label>
              <select 
                value={selectedCoilId || ""} 
                onChange={(e) => setSelectedCoilId(e.target.value || null)}
                className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
              >
                <option value="">Select a coil</option>
                {(coilsInSelected || []).map((c, idx) => (
                  <option key={c.coil_id || idx} value={c.coil_id}>
                    Coil #{idx + 1} - {getCoilWeight(c).toFixed(2)} kg
                  </option>
                ))}
              </select>

              {selectedCoilDoc && (
                <p className="text-[10px] font-mono text-slate-400 uppercase mt-1">
                  Selected Coil Weight: <span className="text-white font-bold">{selectedCoilWeight.toFixed(2)} kg</span>
                </p>
              )}
            </div>
          )}

          {selectedCoilIn && (
            <div className="pt-2">
              <button 
                onClick={() => fetchPrograms(selectedCoilIn)} 
                className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-9 px-4 flex items-center justify-center gap-1.5 transition-colors"
                data-testid="refresh-programs"
              >
                <RefreshCcw className="h-3.5 w-3.5" /> REFRESH
              </button>
            </div>
          )}
        </div>

        {/* Programs List + Create Button */}
        <div className="bg-industrial-s1 border border-industrial-border p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-industrial-border pb-3">
            <h2 className="text-xs uppercase font-sans tracking-wider text-slate-400 flex items-center gap-2 font-semibold">
              <Layers className="h-4 w-4" /> Programs (Grouped by Coil)
            </h2>

            {selectedCoilIn && canEdit && (
              <Dialog
                open={createOpen}
                onOpenChange={(open) => {
                  setCreateOpen(open);
                  if (open) resetCreateForm();
                }}
              >
                <DialogTrigger asChild>
                  <button 
                    className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-9 px-3 flex items-center justify-center gap-1.5 transition-colors text-xs"
                    data-testid="create-program-button"
                  >
                    <Plus className="h-3.5 w-3.5" /> NEW PROGRAM
                  </button>
                </DialogTrigger>

                <DialogContent className="bg-industrial-s1 border border-industrial-border text-white rounded-none max-w-2xl p-6">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-lg font-bold uppercase tracking-wide border-b border-industrial-border pb-2 text-white">Create Program</DialogTitle>
                  </DialogHeader>

                  <form onSubmit={handleCreateProgram} className="space-y-4 text-xs mt-2">
                    <div className="p-3 bg-industrial-s2 border border-industrial-border font-mono text-[11px] space-y-1 text-slate-300">
                      <p><span className="text-slate-500 uppercase">Coil In ID:</span> Selected</p>
                      <p><span className="text-slate-500 uppercase">Coil ID:</span> {selectedCoilId ? "Selected" : "-"}</p>
                      {!selectedCoilId && (
                        <p className="text-led-red font-bold uppercase mt-1">Select a coil first on the main screen.</p>
                      )}
                    </div>

                    <div>
                      <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Allocation Mode</label>
                      <select
                        value={allocationMode}
                        onChange={(e) => {
                          const v = e.target.value;
                          setAllocationMode(v);
                          if (v === "PERCENT") setAllocBySize(seedPercentSplit100(circleSizes));
                          else if (v === "KG") setAllocBySize(syncAllocKeysWithSizes("KG", circleSizes, {}));
                          else setAllocBySize({});
                        }}
                        className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
                      >
                        <option value="EQUAL">EQUAL (auto split)</option>
                        <option value="PERCENT">PERCENT (%)</option>
                        <option value="KG">KG</option>
                      </select>
                    </div>

                    <div>
                      <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-bold">Circle Sizes (inches)</label>
                      <div className="space-y-2 mt-2">
                        {circleSizes.map((size, index) => (
                          <div key={index} className="grid grid-cols-3 gap-2 items-end">
                            <div>
                              <input
                                type="number"
                                step="0.1"
                                placeholder="e.g., 8.0"
                                value={size}
                                onChange={(e) => updateCircleSize(index, e.target.value)}
                                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                                data-testid={`circle-size-${index}`}
                              />
                            </div>

                            <div>
                              <input
                                type="number"
                                step="0.01"
                                placeholder="Rate ₹/kg (optional)"
                                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                                value={
                                  size === "" || !Number.isFinite(Number(size))
                                    ? ""
                                    : (ratesBySize[sizeKey(size)] || "")
                                }
                                onChange={(e) => updateRateForSize(size, e.target.value)}
                                disabled={size === "" || !Number.isFinite(Number(size))}
                              />
                            </div>

                            <div className="flex justify-end">
                              {circleSizes.length > 1 && (
                                <button 
                                  type="button" 
                                  onClick={() => removeCircleSize(index)}
                                  className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase h-10 px-3 flex items-center justify-center transition-colors"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <button 
                        type="button" 
                        onClick={addCircleSize} 
                        className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-9 px-4 flex items-center justify-center gap-1.5 mt-3 transition-colors"
                      >
                        <Plus className="h-4 w-4" /> ADD SIZE
                      </button>

                      <AllocationInputs sizes={circleSizes} />
                    </div>

                    <div>
                      <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Notes (Optional)</label>
                      <input 
                        className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                        value={notes} 
                        onChange={(e) => setNotes(e.target.value)} 
                      />
                    </div>

                    <div className="flex gap-2 justify-end border-t border-industrial-border pt-4 mt-2">
                      <button 
                        type="button" 
                        onClick={() => setCreateOpen(false)}
                        className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                      >
                        CANCEL
                      </button>
                      <button 
                        type="submit" 
                        disabled={loadingCreate || !selectedCoilId} 
                        className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-6 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        data-testid="submit-program"
                      >
                        {loadingCreate ? "CREATING..." : "CREATE PROGRAM"}
                      </button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="space-y-4">
            {!selectedCoilIn ? (
              <div className="text-center py-8 text-slate-500 font-mono text-xs uppercase">Select a coil entry from left panel</div>
            ) : programs.length === 0 ? (
              <div className="text-center py-8 text-slate-500 font-mono text-xs uppercase">No programs defined yet</div>
            ) : (
              <div className="space-y-4">
                {(coilsInSelected || []).map((coil, idx) => {
                  const cid = coil.coil_id;
                  const list = programsByCoilId[cid] || [];

                  return (
                    <div key={cid} className="border border-industrial-border bg-industrial-s2/20 p-4 space-y-3">
                      <div className="flex items-center justify-between border-b border-industrial-border/60 pb-2">
                        <div>
                          <p className="font-mono font-bold text-white text-sm">Coil #{idx + 1}</p>
                          <p className="text-[10px] text-slate-400 font-mono">WEIGHT: {getCoilWeight(coil).toFixed(2)} kg</p>
                        </div>
                        <div>
                          {list.some((p) => p.is_active) ? (
                            <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-led-green bg-industrial-s2 border border-industrial-border px-2 py-0.5">
                              <span className="led-indicator bg-led-green" /> ACTIVE DOCK
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-led-grey bg-industrial-s2 border border-industrial-border px-2 py-0.5">
                              <span className="led-indicator bg-led-grey" /> NO ACTIVE
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-3">
                        {list.length === 0 ? (
                          <p className="text-xs text-slate-500 font-mono uppercase">No programs configured for this coil.</p>
                        ) : (
                          list.map((program) => (
                            <div key={program.id} className="border border-industrial-border/60 bg-industrial-s2/40 p-3 space-y-3">
                              <div className="flex items-center justify-between gap-2 border-b border-industrial-border/30 pb-2">
                                <div className="flex items-center gap-2">
                                  {program.is_active ? (
                                    <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-led-green">
                                      <span className="led-indicator bg-led-green" /> ACTIVE
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-led-grey">
                                      <span className="led-indicator bg-led-grey" /> INACTIVE
                                    </div>
                                  )}
                                  <span className="text-[10px] text-slate-500 font-mono">
                                    {program.created_at ? new Date(program.created_at).toLocaleString() : "-"}
                                  </span>
                                </div>

                                {program.allocation_mode && (
                                  <span className="ml-auto font-mono text-[9px] text-slate-400 border border-industrial-border bg-industrial-s2 px-2 py-0.5 uppercase">
                                    {program.allocation_mode}
                                  </span>
                                )}
                              </div>

                              <div className="flex gap-2 flex-wrap">
                                {(program.circle_sizes || []).map((size, i2) => (
                                  <span key={i2} className="font-mono text-xs text-white border border-industrial-border bg-industrial-s2 px-2 py-1 uppercase">
                                    {size}"
                                  </span>
                                ))}
                              </div>

                              {program.allocations && Array.isArray(program.allocations) && program.allocations.length > 0 && (
                                <div className="text-xs font-mono pt-1">
                                  <p className="font-sans text-[10px] uppercase text-slate-500 mb-1 font-semibold">Allocations:</p>
                                  <div className="flex gap-1.5 flex-wrap">
                                    {program.allocations.map((a, i3) => (
                                      <span key={i3} className="font-mono text-[10px] text-white border border-industrial-border bg-industrial-s2 px-2 py-0.5 uppercase">
                                        {a.circle_size}": {a.value}{program.allocation_mode === "PERCENT" ? "%" : (program.allocation_mode === "KG" ? " kg" : "")}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {program.rates_by_circle_size && (
                                <div className="text-xs font-mono pt-1">
                                  <p className="font-sans text-[10px] uppercase text-slate-500 mb-1 font-semibold">Rates by Size:</p>
                                  <div className="flex gap-1.5 flex-wrap">
                                    {Object.entries(program.rates_by_circle_size).map(([k, v]) => (
                                      <span key={k} className="font-mono text-[10px] text-white border border-industrial-border bg-industrial-s2 px-2 py-0.5 uppercase">
                                        {k}": ₹{v}/kg
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {program.notes && (
                                <p className="text-xs text-slate-400 font-mono mt-2 p-2 bg-industrial-s2 border border-industrial-border/40 break-words">{program.notes}</p>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Programs;
